import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { premiumFontCss } from '@blade/ui-theme'
import { profileAtom } from '@root/entities/profile'
import { useTranslation, type TransKey } from '@root/shared/localization'
import { UnderlineTabs } from '@root/shared/ui-kit'

import { ReferralSideInfo } from './board/referral-board/referral-side-info/referral-side-info'

import type { Profile } from '@root/entities/profile'
import type { ProfileTab } from '@root/features/user/profile/model/profile-tabs'

type Props = {
  tabs: Array<ProfileTab>
  tab: ProfileTab
  onTabChange: (tab: ProfileTab) => void
}

export const ProfilePageTabs = ({ tabs, tab, onTabChange }: Props) => {
  const { t } = useTranslation()
  const [profile] = useAtom(profileAtom)

  const connectionsTab = ['followers', 'following'].includes(tab)
  const isStatistics = tab === 'statistics'

  return (
    <Root>
      <Tabs
        value={tab}
        onChange={onTabChange}
        style={{
          width: connectionsTab ? 'calc(100% - 310px)' : '100%',
          borderBottom: isStatistics
            ? '2px solid var(--background-2)'
            : undefined,
        }}
      >
        {tabs.map((tab) => {
          const count = countTabItems(tab, profile)
          const label = t(TAB_TRANSLATION_MAP[tab])
          return (
            <UnderlineTabs.Tab key={tab} value={tab}>
              <Label>
                {tab === 'premium' ? <PremiumTab>{label}</PremiumTab> : label}{' '}
                {count ? `(${count})` : ''}
              </Label>
            </UnderlineTabs.Tab>
          )
        })}
      </Tabs>
      {tab === 'referral' ? <ReferralSideInfo /> : undefined}
    </Root>
  )
}

const PremiumTab = styled.span`
  ${premiumFontCss}
`

const Root = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 2px solid var(--appearanceA-3);
`

const Tabs = styled(UnderlineTabs<ProfileTab>)`
  padding-inline: 8px;
  text-transform: capitalize;
  display: flex;
  --tab-padding-inline: 5px;
  border-bottom: none;
  position: relative;
  bottom: -1px;
`

const countTabItems = (tab: ProfileTab, profile?: Profile.Profile) => {
  if (profile === undefined) {
    return undefined
  }

  if (tab === 'followers' && profile.followers.length > 0) {
    return profile.followers.length
  }
  if (tab === 'following' && profile.following.length > 0) {
    return profile.following.length
  }

  return undefined
}

const TAB_TRANSLATION_MAP: Record<ProfileTab, TransKey> = {
  premium: 'common.premium',
  statistics: 'profile.statistics',
  coins: 'common.coins_other',
  followers: 'common.follower_other',
  following: 'common.following_other',
  referral: 'referral',
  transactions: 'transaction_other',
  comments: 'common.comments_other',
}

const Label = styled.span`
  white-space: nowrap;
`
